/* Corrida: lobby (andar livre) -> contagem 3-2-1 -> corrida (N voltas) -> resultados.
 * O HOST (ou quem joga solo) dispara 'start' e 'reset'. O resto é local: cada cliente mede o próprio
 * progresso (tot) e o publica junto com a posição; todos calculam o ranking com esses números. */
import { TAU, wrapAngle, poseAtProgress, getTrack } from './Track.js'
import mapCatalog from '../Content/MapCatalog.js'

const LAPS = 3
const COUNTDOWN_MS = 3500

const fmt = (ms) => {
    const t = Math.max(0, ms)
    const m = Math.floor(t / 60000)
    const s = Math.floor((t % 60000) / 1000)
    const c = Math.floor((t % 1000) / 10)
    return `${m}:${String(s).padStart(2, '0')}.${String(c).padStart(2, '0')}`
}
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

export default class Race {
    constructor(experience) {
        this.exp = experience
        this.state = 'lobby' // lobby | countdown | racing
        this.laps = LAPS
        this.total = null // progresso desenrolado (rad); null = ainda sem amostra
        this.lastP = null
        this.fin = null // tempo final (ms) quando termina
        this.goAt = 0
        this.countdownEnd = 0
        this.inputLocked = false
        this._lastBeep = -1
        this._flipT = 0
        this._resultsAt = 0
        this._lastResultsDraw = 0
        this._resultsVisible = false

        // desliga o modo "moedas" antigo (tela inicial, timer, moedas)
        const gm = experience.world?.gameManager
        if (gm) {
            if (gm.collectibleGroup) gm.collectibleGroup.visible = false
            const ui = document.getElementById('game-ui')
            if (ui) ui.style.display = 'none'
        }
        this._buildHUD()
        this._showLobby()
    }

    // ---------- helpers ----------
    get net() { return this.exp.kartNet }
    get car() { return this.exp.simpleCar }
    myId() {
        const n = this.net
        return n && n.connected ? n.selfId() : 'solo'
    }
    canControl() { const n = this.net; return !n || !n.connected || n.isHost }
    now() { return performance.now() }

    // dados que a rede publica junto com a posição
    netState() { return { tot: this.total === null ? null : +this.total.toFixed(3), fin: this.fin } }

    // ---------- comandos ----------
    hostStart() {
        if (!this.canControl() || this.state !== 'lobby') return
        const n = this.net
        if (n && n.connected && n.mode === 'ws') { n.wsRace('start'); return } // o servidor monta o grid e manda pra todos (inclusive eu)
        const ids = n && n.connected && n.isHost ? ['host', ...n.guestIds.keys()] : ['host']
        const grid = {}
        ids.forEach((id, i) => { grid[id] = i })
        const mapLaps = mapCatalog.active?.laps || LAPS
        n?.sendRace({ type: 'race', cmd: 'start', laps: mapLaps, grid })
        this._begin(grid, mapCatalog.active?.laps || LAPS)
    }

    hostReset() {
        if (!this.canControl()) return
        if (this.net && this.net.connected && this.net.mode === 'ws') { this.net.wsRace('reset'); return }
        this.net?.sendRace({ type: 'race', cmd: 'reset' })
        this._toLobby()
    }

    onNetRace(m) {
        if (m.cmd === 'start' && m.grid) this._begin(m.grid, m.laps || LAPS)
        else if (m.cmd === 'reset') this._toLobby()
        else if (m.cmd === 'sync' && m.state && m.state !== 'lobby') this._setCenter('Corrida em andamento — aguarde a próxima largada', 4000)
    }

    snapshot() { return { state: this.state } }

    // o dono da sala mudou (saiu/caiu): atualiza botões de iniciar / abortar
    onOwnerChange() {
        if (this.state === 'lobby') { if (this._lobby) this._updateLobbyList(true) }
        else if (this._abortBtn) this._abortBtn.style.display = this.canControl() ? 'block' : 'none'
        if (this._resultsVisible) this._drawResults()
    }

    // ---------- estados ----------
    _begin(grid, laps) {
        const car = this.car
        if (!car) return
        this.laps = laps
        const id = this.myId()
        car.gridSlot = typeof grid[id] === 'number' ? grid[id] : Object.keys(grid).length
        car.resetPosition()
        this._releaseControls()
        this.total = null
        this.lastP = null
        this.fin = null
        this._flipT = 0
        this._trackProgress(car.vehicle.chassisBody.position)
        this.state = 'countdown'
        this.countdownEnd = this.now() + COUNTDOWN_MS
        this.inputLocked = true
        this._lastBeep = -1
        this._hideResults()
        this._hideLobby()
        this.exp.items?.reset()
        this._abortBtn.style.display = this.canControl() ? 'block' : 'none'
    }

    _toLobby() {
        this.state = 'lobby'
        this.inputLocked = false
        this.total = null
        this.lastP = null
        this.fin = null
        this._hideResults()
        this.exp.items?.reset()
        this._setCenter('')
        this._abortBtn.style.display = 'none'
        this.car?.resetPosition()
        this._releaseControls()
        this._showLobby()
    }

    _releaseControls() {
        const v = this.car?.vehicle
        if (!v) return
        v.setWheelForce(0, 0); v.setWheelForce(0, 1)
        v.setSteeringValue(0, 2); v.setSteeringValue(0, 3)
    }

    _hold() {
        const v = this.car.vehicle
        v.chassisBody.velocity.setZero()
        v.chassisBody.angularVelocity.setZero()
        v.wheelBodies?.forEach((w) => { w.velocity.setZero(); w.angularVelocity.setZero() })
    }

    // volta pra pista no ponto onde estava (capotou, caiu, ficou preso) — sem ganhar/perder progresso
    respawn() {
        const car = this.car
        if (!car) return
        if (this.total === null) { car.resetPosition(); return }
        const p = poseAtProgress(this.total)
        car.placeAt(p.x, p.z, p.yaw)
        this.lastP = wrapAngle(this.total)
        this._flipT = 0
    }

    // ---------- progresso ----------
    _trackProgress(pos) {
        const r = Math.hypot(pos.x, pos.z)
        const T = getTrack()
        if (r < T.bandMin || r > T.bandMax) return // fora da pista: congela
        const p = wrapAngle(Math.atan2(pos.z, pos.x) - T.startAngle)
        if (this.lastP === null || this.total === null) { this.lastP = p; this.total = p; return }
        const d = wrapAngle(p - this.lastP)
        this.lastP = p
        if (Math.abs(d) > 0.6) return // salto/atalho: sem crédito
        this.total += d
    }

    get lap() {
        if (this.total === null || this.total < 0) return 1
        return Math.min(this.laps, Math.floor(this.total / TAU) + 1)
    }

    standings() {
        const net = this.net
        const list = [{ id: this.myId(), me: true, name: net?.myName || 'Você', tot: this.total ?? -9, fin: this.fin }]
        if (net) for (const [id, r] of net.remotes) list.push({ id, me: false, name: r.name, tot: r.tot ?? -9, fin: r.fin ?? null })
        list.sort((a, b) => {
            if (a.fin != null && b.fin != null) return a.fin - b.fin
            if (a.fin != null) return -1
            if (b.fin != null) return 1
            return b.tot - a.tot
        })
        return list
    }

    // ---------- loop ----------
    update() {
        const car = this.car
        if (!car?.vehicle?.chassisBody) return
        const body = car.vehicle.chassisBody
        const t = this.now()

        // capotou / caiu da pista
        const up = body.quaternion.vmult({ x: 0, y: 1, z: 0 })
        this._flipT = up.y < 0.3 ? this._flipT + 1 : 0
        if (body.position.y < -3 || this._flipT > 120) this.respawn()

        if (this.state === 'lobby') { this._updateLobbyList(); return }

        if (this.state === 'countdown') {
            this._hold()
            const left = this.countdownEnd - t
            const n = Math.ceil((left - 500) / 1000) // 3,2,1
            if (left > 500) {
                if (n !== this._lastBeep) { this._lastBeep = n; this._setCenter(String(n)); this._beep(440, 0.12) }
            } else {
                this._lastBeep = 0
                this._setCenter('JÁ!', 900)
                this._beep(880, 0.35)
                this.state = 'racing'
                this.goAt = t
                this.inputLocked = false
                this.exp.touchControls?.apply?.() // retoma o que o jogador já está segurando
            }
            this._updateHUD(0)
            return
        }

        // racing
        this._trackProgress(body.position)
        const elapsed = this.fin ?? t - this.goAt
        if (this.fin === null && this.total !== null && this.total >= this.laps * TAU) {
            this.fin = t - this.goAt
            this._beep(988, 0.4)
            this._setCenter('🏁 CHEGOU!', 1600)
            this._resultsAt = t + 1600
        }
        if (this.fin !== null && this._resultsAt && t >= this._resultsAt) { this._resultsAt = 0; this._showResults() }
        if (this._resultsVisible && t - this._lastResultsDraw > 400) this._drawResults()
        this._updateHUD(elapsed)
    }

    _beep(f, d) { try { this.exp.world?.gameManager?.playSound(f, d) } catch (_) { /* sem áudio */ } }

    // ---------- HUD ----------
    _buildHUD() {
        const css = document.createElement('style')
        css.textContent = `
        #race-hud{position:fixed;inset:0;pointer-events:none;z-index:55;font-family:system-ui,sans-serif;color:#fff}
        #race-stats{position:absolute;top:56px;right:10px;text-align:right;text-shadow:0 2px 6px #000;display:none}
        #race-pos{font:900 44px system-ui;line-height:1}
        #race-pos small{font-size:20px;opacity:.8}
        #race-lap,#race-time{font:700 15px system-ui}
        #race-center{position:absolute;top:32%;left:0;right:0;text-align:center;font:900 min(22vw,120px) system-ui;text-shadow:0 4px 14px #000;transition:opacity .3s}
        #race-center.small{font-size:min(6vw,24px);top:20%}
        .race-card{position:absolute;background:rgba(0,0,0,.62);border:1px solid rgba(255,255,255,.25);border-radius:14px;padding:12px 14px;pointer-events:auto}
        #race-lobby{top:132px;left:10px;width:min(62vw,240px);font-size:13px}
        #race-lobby b{font-size:14px}
        #race-lobby ul{list-style:none;margin:6px 0 8px;padding:0;max-height:28vh;overflow:auto}
        #race-results{top:50%;left:50%;transform:translate(-50%,-50%);width:min(92vw,360px);display:none;text-align:center}
        #race-results h2{margin:0 0 10px;font-size:20px}
        #race-results table{width:100%;border-collapse:collapse;font-size:15px}
        #race-results td{padding:5px 4px;text-align:left}
        #race-results td:last-child{text-align:right;font-variant-numeric:tabular-nums}
        #race-results tr.me{color:#ffe14a;font-weight:700}
        .race-btn{display:block;width:100%;margin-top:8px;padding:11px;font:700 15px system-ui;border:0;border-radius:10px;cursor:pointer;background:linear-gradient(180deg,#43d67a,#2ba95c);color:#06281a}
        #race-abort{position:absolute;top:132px;left:10px;display:none;pointer-events:auto;padding:6px 10px;font:600 12px system-ui;border-radius:8px;border:1px solid rgba(255,255,255,.4);background:rgba(0,0,0,.5);color:#fff;cursor:pointer}`
        document.head.appendChild(css)

        const hud = document.createElement('div')
        hud.id = 'race-hud'
        hud.innerHTML = `
          <div id="race-stats"><div id="race-pos">1º<small>/1</small></div><div id="race-lap"></div><div id="race-time"></div></div>
          <div id="race-center"></div>
          <div id="race-lobby" class="race-card"></div>
          <button id="race-abort">↩ Voltar ao lobby</button>
          <div id="race-results" class="race-card"></div>`
        document.body.appendChild(hud)
        this._stats = hud.querySelector('#race-stats')
        this._posEl = hud.querySelector('#race-pos')
        this._lapEl = hud.querySelector('#race-lap')
        this._timeEl = hud.querySelector('#race-time')
        this._center = hud.querySelector('#race-center')
        this._lobby = hud.querySelector('#race-lobby')
        this._results = hud.querySelector('#race-results')
        this._abortBtn = hud.querySelector('#race-abort')
        this._abortBtn.addEventListener('click', () => this.hostReset())
    }

    _setCenter(text, hideAfter) {
        this._center.textContent = text
        this._center.classList.toggle('small', text.length > 6)
        this._center.style.opacity = text ? '1' : '0'
        clearTimeout(this._centerT)
        if (text && hideAfter) this._centerT = setTimeout(() => { this._center.style.opacity = '0' }, hideAfter)
    }

    _showLobby() {
        this._lobby.style.display = 'block'
        this._stats.style.display = 'none'
        this._lobbyKey = ''
        this._updateLobbyList(true)
    }
    _hideLobby() { this._lobby.style.display = 'none'; this._stats.style.display = 'block' }

    _updateLobbyList(force) {
        const n = this.net
        const names = [n?.myName || 'Você (eu)']
        if (n) for (const [, r] of n.remotes) names.push(r.name)
        const can = this.canControl()
        const key = names.join('|') + '|' + can + '|' + (n?.roomCode || '')
        if (!force && key === this._lobbyKey) return
        this._lobbyKey = key
        const code = n?.connected && n.roomCode ? `Sala <b>${esc(n.roomCode)}</b> · ` : ''
        this._lobby.innerHTML = `${code}<b>${names.length}</b> piloto${names.length > 1 ? 's' : ''}
          <ul>${names.map((x, i) => `<li>${i === 0 ? '🏎️' : '🚗'} ${esc(x)}</li>`).join('')}</ul>
          ${can ? '<button class="race-btn" id="race-go">🏁 INICIAR CORRIDA</button>'
                : '<div style="opacity:.8">Aguardando o host iniciar…</div>'}
          <div style="opacity:.6;margin-top:6px">Treine à vontade enquanto isso!</div>`
        this._lobby.querySelector('#race-go')?.addEventListener('click', () => this.hostStart())
    }

    _updateHUD(elapsed) {
        const st = this.standings()
        const pos = st.findIndex((s) => s.me) + 1
        this._posEl.innerHTML = `${pos}º<small>/${st.length}</small>`
        this._lapEl.textContent = `VOLTA ${this.lap}/${this.laps}`
        this._timeEl.textContent = fmt(elapsed)
    }

    _showResults() {
        this._resultsVisible = true
        this._results.style.display = 'block'
        this._drawResults()
    }
    _hideResults() { this._resultsVisible = false; this._resultsAt = 0; this._results.style.display = 'none' }

    _drawResults() {
        this._lastResultsDraw = this.now()
        const medals = ['🥇', '🥈', '🥉']
        const rows = this.standings().map((s, i) => `<tr class="${s.me ? 'me' : ''}"><td>${medals[i] || i + 1 + 'º'} ${esc(s.name)}</td><td>${s.fin != null ? fmt(s.fin) : 'correndo…'}</td></tr>`).join('')
        this._results.innerHTML = `<h2>🏆 Resultado</h2><table>${rows}</table>` +
            (this.canControl() ? '<button class="race-btn" id="race-again">🔁 NOVA CORRIDA</button>'
                : '<div style="opacity:.8;margin-top:10px">Aguardando o host…</div>')
        this._results.querySelector('#race-again')?.addEventListener('click', () => this.hostReset())
    }
}
